/**
 * IoT Support Jobs - Card View
 * Displays job cards from in-organisation API in the same card layout as report/card-view.js
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Spin, message, Card, Checkbox } from 'antd'
import axios from 'util/Api'
import Board from './report/Board'
import FilterBar from './report/FilterBar'
import StatusFilters from './report/StatusFilters'
import DetailsDrawer from './report/DetailsDrawer'
import { parseDateOnly, isExpectedCompletionPast, isReviewDatePast } from './report/utils'
import './report/styles.css'

const IN_ORGANISATION_LIMIT = 1000

function buildJobCardUrl (record) {
    if (!record || !record._id) {
        return ''
    }

    const currentHost = window.location.hostname
    const currentProtocol = window.location.protocol

    const hostParts = currentHost.split('.')
    const domain = hostParts.length > 2 ? hostParts.slice(1).join('.') : currentHost

    const organisationSlug = record.organisation && record.organisation.slug
    if (!organisationSlug) {
        return `${currentProtocol}//${currentHost}/job-card/report/${record._id}`
    }

    const subdomain = `${organisationSlug}.${domain}`
    const path = `/job-card/report/${record._id}`
    return `${currentProtocol}//${subdomain}${path}`
}

function mapRecordToCard (record) {
    const jobCardUrl = buildJobCardUrl(record)
    return {
        jobCardId: record._id || record.id || '',
        jobCardNo: record.jobCardNo || record.job_card_no || '',
        assetCode: record.assetCode || record.asset_code || '',
        ownerCode: record.ownerCode || record.owner_code || '',
        assetSubType: record.equipmentSubType || record.assetSubType || '',
        location: record.location || '',
        status: record.status || '',
        expectedCompletion: record.expectedCompletion || record.expected_completion || '',
        presentStatus: record.presentStatus || record.present_status || '',
        presentStatusColor: record.presentStatusColor || record.present_status_color || '',
        presentStatusRemark: record.presentStatusRemark || record.present_status_remark || '',
        detailsOfComplaint: record.detailsOfComplaint || record.details_of_complaint || '',
        causeOfDamage: record.causeOfDamage || record.cause_of_damage || '',
        subLocation: record.subLocation || record.sub_location || '',
        complaintDate: record.complaintDate || record.complaint_date || '',
        owner: record.owner || '',
        assetType: record.assetType || record.asset_type || '',
        complaintNo: record.complaintNo || record.complaint_no || '',
        remark: record.remark || '',
        cancelDate: record.cancelDate || record.cancel_date || '',
        uncancelDate: record.uncancelDate || record.uncancel_date || '',
        closureDate: record.closureDate || record.closure_date || '',
        followUp: record.followUp || record.follow_up || '',
        followUpDate: record.followUpDate || record.follow_up_date || '',
        reviewDate: record.reviewDate || record.review_date || '',
        createdAt: record.createdAt || record.created_at || '',
        updatedAt: record.updatedAt || record.updated_at || '',
        totalAmount: record.totalAmount || record.total_amount || '',
        pendingInvoice: record.pendingInvoice || record.pending_invoice || '',
        assetUnservicable: record.assetUnservicable || record.asset_unservicable || '',
        statusTitle: record.statusTitle || record.status_title || '',
        jobCardUrl,
        organisationName: record.organisation && record.organisation.name ? record.organisation.name : ''
    }
}

function IotSupportJobsCardView (props) {
    const [jobCards, setJobCards] = useState([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [activeFilterTypes, setActiveFilterTypes] = useState(new Set(['presentStatus', 'organisation']))
    const [enabledFilterValues, setEnabledFilterValues] = useState(new Map())
    const [expandedColumnId, setExpandedColumnId] = useState(null)
    const [showUnserviceableOnly, setShowUnserviceableOnly] = useState(false)
    const [showExpiredOnly, setShowExpiredOnly] = useState(false)
    const [showClosedPIOnly, setShowClosedPIOnly] = useState(false)
    const [onlyOpen, setOnlyOpen] = useState(true)
    const [onlyClosed, setOnlyClosed] = useState(false)
    const [selectedCard, setSelectedCard] = useState(null)
    const [detailsVisible, setDetailsVisible] = useState(false)

    const status = useMemo(() => {
        if (onlyOpen && !onlyClosed) return 1
        if (onlyClosed && !onlyOpen) return 2
        return 0
    }, [onlyOpen, onlyClosed])

    const loadData = useCallback(async () => {
        try {
            setLoading(true)
            let query = `/job-card/in-organisation/?limit=${IN_ORGANISATION_LIMIT}&offset=0`
            if (status !== 0) {
                query += `&status=${status}`
            }

            const response = await axios.get(query)

            let data = []
            if (response && response.data && response.data.data && Array.isArray(response.data.data)) {
                data = response.data.data
            }

            const mappedCards = data.map((record) => mapRecordToCard(record))
            setJobCards(mappedCards)
            initializeFilterValues(mappedCards)
        } catch (error) {
            console.error('Error loading job cards:', error)
            message.error(error.response && error.response.data && error.response.data.message
                ? error.response.data.message
                : 'Failed to load job cards.')
            setJobCards([])
        } finally {
            setLoading(false)
        }
    }, [status])

    const initializeFilterValues = (cards) => {
        const newEnabledValues = new Map()
        activeFilterTypes.forEach((filterType) => {
            const allValues = new Set()
            cards.forEach((c) => {
                const value = getFilterValue(c, filterType)
                if (value) allValues.add(value)
            })
            newEnabledValues.set(filterType, new Set(allValues))
        })
        setEnabledFilterValues(newEnabledValues)
    }

    const getFilterFieldName = (filterType) => {
        const fieldMap = {
            presentStatus: 'presentStatus',
            location: 'location',
            assetType: 'assetType',
            assetSubType: 'assetSubType',
            subLocation: 'subLocation',
            organisation: 'organisationName'
        }
        return fieldMap[filterType] || null
    }

    const getFilterValue = (card, filterType) => {
        const fieldName = getFilterFieldName(filterType)
        if (!fieldName) return ''
        return (card[fieldName] || '').trim()
    }

    const getFilterTypeLabel = (filterType) => {
        const labelMap = {
            presentStatus: 'Present Status',
            location: 'Location',
            assetType: 'Asset Type',
            assetSubType: 'Asset Sub Type',
            subLocation: 'Sub Location',
            organisation: 'Organisation'
        }
        return labelMap[filterType] || filterType
    }

    useEffect(() => {
        loadData()
    }, [loadData])

    const handleOnlyOpenChange = (e) => {
        setOnlyOpen(e.target.checked)
    }

    const handleOnlyClosedChange = (e) => {
        setOnlyClosed(e.target.checked)
    }

    const matchesFilters = useCallback((card) => {
        for (const filterType of activeFilterTypes) {
            const cardValue = getFilterValue(card, filterType)
            if (!enabledFilterValues.has(filterType)) continue

            const enabledValues = enabledFilterValues.get(filterType)
            const allValues = new Set()
            jobCards.forEach((c) => {
                const val = getFilterValue(c, filterType)
                if (val) allValues.add(val)
            })

            if (enabledValues.size === 0) return false
            if (enabledValues.size > 0 && enabledValues.size < allValues.size) {
                if (!enabledValues.has(cardValue)) return false
            }
        }

        if (searchTerm) {
            const haystack = (
                card.jobCardNo +
                ' ' +
                (card.detailsOfComplaint || '') +
                ' ' +
                (card.assetType || '') +
                ' ' +
                (card.assetSubType || '') +
                ' ' +
                (card.assetCode || '') +
                ' ' +
                (card.location || '') +
                ' ' +
                (card.subLocation || '') +
                ' ' +
                (card.organisationName || '')
            ).toLowerCase()
            if (!haystack.includes(searchTerm.toLowerCase())) return false
        }

        if (showUnserviceableOnly) {
            const isUnserviceable = (card.assetUnservicable || '').toString().toLowerCase().trim() === 'true'
            if (!isUnserviceable) return false
        }

        if (showExpiredOnly) {
            if (!isReviewDatePast(card)) return false
        }

        if (showClosedPIOnly) {
            if ((card.statusTitle || '').trim() !== 'ClosedPendingInvoice') return false
        }

        return true
    }, [activeFilterTypes, enabledFilterValues, jobCards, searchTerm, showUnserviceableOnly, showExpiredOnly, showClosedPIOnly])

    const filteredCards = useMemo(() => {
        return jobCards.filter(matchesFilters)
    }, [jobCards, matchesFilters])

    const handleFilterTypeToggle = (filterType, isChecked) => {
        const newActiveTypes = new Set(activeFilterTypes)
        const newEnabledValues = new Map(enabledFilterValues)

        if (isChecked) {
            newActiveTypes.add(filterType)
            if (!newEnabledValues.has(filterType)) {
                const allValues = new Set()
                jobCards.forEach((c) => {
                    const val = getFilterValue(c, filterType)
                    if (val) allValues.add(val)
                })
                newEnabledValues.set(filterType, new Set(allValues))
            }
        } else {
            newActiveTypes.delete(filterType)
            newEnabledValues.delete(filterType)
        }

        setActiveFilterTypes(newActiveTypes)
        setEnabledFilterValues(newEnabledValues)
    }

    const handleToggleFilterValue = (filterType, value) => {
        const newEnabledValues = new Map(enabledFilterValues)
        if (!newEnabledValues.has(filterType)) {
            newEnabledValues.set(filterType, new Set())
        }
        const enabledValues = newEnabledValues.get(filterType)
        if (enabledValues.has(value)) {
            enabledValues.delete(value)
        } else {
            enabledValues.add(value)
        }
        const allValues = new Set()
        jobCards.forEach((c) => {
            const val = getFilterValue(c, filterType)
            if (val) allValues.add(val)
        })
        if (enabledValues.size === 0) {
            allValues.forEach((v) => enabledValues.add(v))
        }
        setEnabledFilterValues(newEnabledValues)
    }

    const handleSelectAll = (filterType) => {
        const newEnabledValues = new Map(enabledFilterValues)
        if (!newEnabledValues.has(filterType)) {
            newEnabledValues.set(filterType, new Set())
        }
        const enabledValues = newEnabledValues.get(filterType)
        const allValues = new Set()
        jobCards.forEach((c) => {
            const val = getFilterValue(c, filterType)
            if (val) allValues.add(val)
        })
        allValues.forEach((v) => enabledValues.add(v))
        setEnabledFilterValues(newEnabledValues)
    }

    const handleSelectNone = (filterType) => {
        const newEnabledValues = new Map(enabledFilterValues)
        newEnabledValues.set(filterType, new Set())
        setEnabledFilterValues(newEnabledValues)
    }

    const handleReset = () => {
        setSearchTerm('')
        setShowUnserviceableOnly(false)
        setShowExpiredOnly(false)
        setShowClosedPIOnly(false)
        const newEnabledValues = new Map()
        activeFilterTypes.forEach((filterType) => {
            const allValues = new Set()
            jobCards.forEach((c) => {
                const val = getFilterValue(c, filterType)
                if (val) allValues.add(val)
            })
            newEnabledValues.set(filterType, new Set(allValues))
        })
        setEnabledFilterValues(newEnabledValues)
    }

    const handleDownloadCsv = () => {
        if (filteredCards.length === 0) {
            message.warning('No cards to export. Please adjust your filters.')
            return
        }

        const fieldNames = [
            'Organisation',
            'Job Card No',
            'Asset Code',
            'Owner Code',
            'Asset Sub Type',
            'Location',
            'Status',
            'Present Status',
            'Details Of Complaint',
            'Complaint Date',
            'Asset Type',
            'Complaint No',
            'Job Card URL'
        ]

        const cardToRow = (card) => {
            return fieldNames.map(fieldName => {
                const fieldMap = {
                    'Organisation': card.organisationName || '',
                    'Job Card No': card.jobCardNo || '',
                    'Asset Code': card.assetCode || '',
                    'Owner Code': card.ownerCode || '',
                    'Asset Sub Type': card.assetSubType || '',
                    'Location': card.location || '',
                    'Status': card.status || '',
                    'Present Status': card.presentStatus || '',
                    'Details Of Complaint': card.detailsOfComplaint || '',
                    'Complaint Date': card.complaintDate || '',
                    'Asset Type': card.assetType || '',
                    'Complaint No': card.complaintNo || '',
                    'Job Card URL': card.jobCardUrl || ''
                }
                const value = fieldMap[fieldName] || ''
                return value.toString().replace(/;/g, ';;').replace(/\n/g, ' ')
            })
        }

        const csvRows = [
            fieldNames.join(';'),
            ...filteredCards.map(card => cardToRow(card).join(';'))
        ]
        const csvContent = csvRows.join('\n')
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', `iot-support-job-cards_${new Date().toISOString().split('T')[0]}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    const handleCardClick = (card) => {
        setSelectedCard(card)
        setDetailsVisible(true)
    }

    const handleToggleExpand = (stageId) => {
        setExpandedColumnId(expandedColumnId === stageId ? null : stageId)
    }

    if (loading && jobCards.length === 0) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin size="large" tip="Loading job cards..." />
            </div>
        )
    }

    return (
        <Card
            className="gx-card-metrics"
            style={{ backgroundColor: 'transparent', padding: '0', margin: '0' }}
            bodyStyle={{ padding: '0', margin: '0' }}
        >
            <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 500 }}>Status:</span>
                <Checkbox
                    checked={onlyOpen}
                    onChange={handleOnlyOpenChange}
                >
                    Only Open
                </Checkbox>
                <Checkbox
                    checked={onlyClosed}
                    onChange={handleOnlyClosedChange}
                >
                    Only Closed
                </Checkbox>
            </div>

            <FilterBar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                activeFilterTypes={activeFilterTypes}
                onFilterTypeToggle={handleFilterTypeToggle}
                showUnserviceableOnly={showUnserviceableOnly}
                onUnserviceableToggle={setShowUnserviceableOnly}
                showExpiredOnly={showExpiredOnly}
                onExpiredToggle={setShowExpiredOnly}
                showClosedPIOnly={showClosedPIOnly}
                onClosedPIToggle={setShowClosedPIOnly}
                onReset={handleReset}
                onDownloadCsv={handleDownloadCsv}
                onRefresh={loadData}
                loading={loading}
            />

            <StatusFilters
                activeFilterTypes={activeFilterTypes}
                enabledFilterValues={enabledFilterValues}
                jobCards={jobCards}
                searchTerm={searchTerm}
                showUnserviceableOnly={showUnserviceableOnly}
                showExpiredOnly={showExpiredOnly}
                showClosedPIOnly={showClosedPIOnly}
                onToggleFilterValue={handleToggleFilterValue}
                onSelectAll={handleSelectAll}
                onSelectNone={handleSelectNone}
                getFilterFieldName={getFilterFieldName}
                getFilterValue={getFilterValue}
                getFilterTypeLabel={getFilterTypeLabel}
            />

            <Spin spinning={loading} tip="Refreshing...">
                <Board
                    jobCards={filteredCards}
                    expandedColumnId={expandedColumnId}
                    onCardClick={handleCardClick}
                    onToggleExpand={handleToggleExpand}
                />
            </Spin>

            <DetailsDrawer
                card={selectedCard}
                visible={detailsVisible}
                onClose={() => {
                    setDetailsVisible(false)
                    setSelectedCard(null)
                }}
            />
        </Card>
    )
}

export default IotSupportJobsCardView
